import { zodResolver } from '@hookform/resolvers/zod';
import {
  inspectionsControllerDeleteAttachment,
  inspectionsControllerFindOne,
  inspectionsControllerUpdate,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CloudUpload, FileUp, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../components/ui/button';
import { api } from '../../../../lib/ky';
import { cn } from '../../../../lib/utils';
import type { PermitDetail } from './types';

interface Props {
  permit: PermitDetail;
  onUpdated: () => void;
  can: (perm: string) => boolean;
  currentUserId?: string | null;
  startForm?: boolean;
  onFormStarted?: () => void;
}

interface InspectionWithUrls {
  id: string;
  assignedInspectorId: string;
  status: string;
  findings?: string | null;
  recommendations?: string | null;
  completedAt?: string | null;
  attachments: Array<{
    id: string;
    fileName: string;
    signedUrl?: string | null;
    createdAt: string;
  }>;
}

const INSPECTION_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  PENDING: { label: 'Pending', className: 'bg-gray-100 text-gray-700' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-yellow-100 text-yellow-700' },
  COMPLETED: { label: 'Completed', className: 'bg-green-100 text-green-700' },
};

const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png';
const ACCEPTED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];

const inspectionSchema = z.object({
  findings: z.string(),
  recommendations: z.string(),
});
type InspectionValues = z.infer<typeof inspectionSchema>;

export function InspectionTab({
  permit,
  onUpdated,
  can,
  currentUserId,
  startForm,
  onFormStarted,
}: Props) {
  const qc = useQueryClient();
  const inspectionKey = ['inspection', permit.id];
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const isAssigned = permit.inspection?.assignedInspectorId === currentUserId;
  const isInProgress = permit.inspection?.status === 'IN_PROGRESS';
  const canEditInspection = isAssigned && isInProgress;
  const hasExistingContent = !!(permit.inspection?.findings || permit.inspection?.recommendations);
  const isCompleted = permit.inspection?.status === 'COMPLETED';

  const [formVisible, setFormVisible] = useState(startForm || hasExistingContent || isCompleted);

  useEffect(() => {
    if (startForm) setFormVisible(true);
  }, [startForm]);

  const { data: inspection, isLoading } = useQuery({
    queryKey: inspectionKey,
    queryFn: () =>
      inspectionsControllerFindOne({
        permitId: permit.id,
      }) as unknown as Promise<InspectionWithUrls>,
    enabled: !!permit.inspection,
    retry: false,
  });

  const { register, handleSubmit } = useForm<InspectionValues>({
    resolver: zodResolver(inspectionSchema),
    defaultValues: {
      findings: permit.inspection?.findings ?? '',
      recommendations: permit.inspection?.recommendations ?? '',
    },
  });

  const saveMutation = useMutation({
    mutationFn: (data: InspectionValues) =>
      inspectionsControllerUpdate({
        permitId: permit.id,
        requestBody: { findings: data.findings, recommendations: data.recommendations },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inspectionKey });
      onUpdated();
    },
  });

  const uploadAttachment = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`permits/${permit.id}/inspections/attachments`, { body: fd }).json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inspectionKey });
      onUpdated();
    },
  });

  const deleteAttachment = useMutation({
    mutationFn: (attId: string) =>
      inspectionsControllerDeleteAttachment({ permitId: permit.id, attachmentId: attId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inspectionKey });
      onUpdated();
    },
  });

  function handleFile(file: File) {
    if (!ACCEPTED_MIME.includes(file.type)) return;
    uploadAttachment.mutate(file);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function handlePaste(e: React.ClipboardEvent) {
    const file = Array.from(e.clipboardData.files)[0];
    if (file) handleFile(file);
  }

  // ── No inspection assigned yet ──────────────────────────────────────────
  if (!permit.inspection) {
    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-12 text-center text-sm text-muted-foreground">
        No inspection record.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-6 space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-16 rounded-xl bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  const statusCfg = INSPECTION_STATUS_CONFIG[permit.inspection.status] ?? {
    label: permit.inspection.status,
    className: 'bg-gray-100 text-gray-700',
  };

  // ── Assigned inspector hasn't started the form yet ───────────────────────
  if (canEditInspection && !formVisible) {
    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-12 text-center">
        <p className="mb-4 text-sm text-muted-foreground">
          Ready to create your inspection report.
        </p>
        <Button
          variant="primary"
          onClick={() => {
            setFormVisible(true);
            onFormStarted?.();
          }}
        >
          Create Inspection
        </Button>
      </div>
    );
  }

  // ── Inspection form (editable or read-only) ──────────────────────────────
  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500">Assigned Inspector</p>
          <p className="mt-0.5 text-sm font-medium text-gray-900">
            {permit.assignedInspector?.name ?? 'Unknown'}
          </p>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusCfg.className}`}
        >
          {statusCfg.label}
        </span>
      </div>

      <form onSubmit={handleSubmit((d) => saveMutation.mutate(d))} className="space-y-4">
        <div>
          <label className="mb-1 block text-xs text-gray-500">Findings</label>
          <textarea
            className="w-full resize-y rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 hover:border-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 transition-all disabled:bg-gray-50 disabled:opacity-70 disabled:text-gray-500"
            rows={4}
            disabled={!canEditInspection}
            {...register('findings')}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs text-gray-500">Recommendations</label>
          <textarea
            className="w-full resize-y rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 hover:border-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 transition-all disabled:bg-gray-50 disabled:opacity-70 disabled:text-gray-500"
            rows={4}
            disabled={!canEditInspection}
            {...register('recommendations')}
          />
        </div>

        {can('inspection:view') && (
          <div>
            <label className="mb-2 block text-xs text-gray-500">Attachments</label>

            {canEditInspection && (
              <div
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
                onPaste={handlePaste}
                className={cn(
                  'mb-3 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-6 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
                  isDragOver
                    ? 'border-brand-400 bg-brand-50'
                    : 'border-gray-200 bg-gray-50 hover:border-brand-300 hover:bg-brand-50/40',
                  uploadAttachment.isPending && 'pointer-events-none opacity-60',
                )}
              >
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-xl transition-colors',
                    isDragOver ? 'bg-brand-100 text-brand-600' : 'bg-gray-100 text-gray-400',
                  )}
                >
                  {uploadAttachment.isPending ? (
                    <FileUp size={20} className="animate-bounce" />
                  ) : (
                    <CloudUpload size={20} />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {uploadAttachment.isPending
                      ? 'Uploading…'
                      : 'Drop file here, paste, or click to browse'}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-400">PDF, JPG, PNG accepted</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_TYPES}
                  className="sr-only"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                    e.target.value = '';
                  }}
                />
              </div>
            )}

            {!inspection?.attachments || inspection.attachments.length === 0 ? (
              <p className="text-xs text-gray-400">No attachments yet.</p>
            ) : (
              <ul className="space-y-2">
                {inspection.attachments.map((att) => (
                  <li
                    key={att.id}
                    className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm"
                  >
                    <a
                      href={att.signedUrl ?? '#'}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-brand-600 hover:underline"
                    >
                      {att.fileName}
                    </a>
                    {canEditInspection &&
                      (deleteConfirm === att.id ? (
                        <div className="ml-2 flex shrink-0 items-center gap-1">
                          <span className="text-xs text-gray-500">Delete?</span>
                          <button
                            type="button"
                            onClick={() => {
                              deleteAttachment.mutate(att.id);
                              setDeleteConfirm(null);
                            }}
                            className="text-xs text-red-600"
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(null)}
                            className="text-xs text-gray-500"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(att.id)}
                          className="ml-2 shrink-0 rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      ))}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {canEditInspection && (
          <div className="pt-3 mt-1 border-t border-gray-100 flex justify-end">
            <Button variant="outline" size="sm" type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
